import { COMBINED_2015_2022_METHOD_VERSION } from '../utils/vmtCombinedCore';
import { CALIFORNIA_STATE_GEOFIPS, fetchCaliforniaStateLdvVmtSeries } from './californiaGhgInventoryVmtService';
import { DataService } from './dataService';
import { supabaseClient } from './supabase';
import {
  clearVMTData,
  getGlobalVMTData,
  getGlobalVMTDataLocationKey,
  getInFlightVMT,
  setGlobalVMTData,
  setInFlightVMT,
  type VMTDataPoint,
} from './vmtDataCache';

export { clearVMTData };
export type { VMTDataPoint };

export interface VMTDataResult {
  years: number[];
  values: number[];
  sources: string[];
  providers: string[];
}

/**
 * VMT Data Service for GHG Inventory
 * This service handles VMT data from the Transportation page (with user customizations)
 * and provides it to the GHG Inventory for emissions calculations
 */

const VMT_CA_KEYS = new Set(['california', 'state of california', 'ca']);

export function normalizeVmtLocationKey(location: string): string {
  return location.trim().toLowerCase().replace(/\s+/g, ' ');
}

function vmtLocationKeysMatch(stored: string | null | undefined, requested: string): boolean {
  if (!stored) return false;
  const a = normalizeVmtLocationKey(stored);
  const b = normalizeVmtLocationKey(requested);
  if (a === b) return true;
  if (VMT_CA_KEYS.has(a) && VMT_CA_KEYS.has(b)) return true;
  return false;
}

function vmtCacheKey(location: string, geofips?: number, emfacVersion: '2021' | '2025' = '2025'): string {
  return `${location}|${geofips ?? ''}|${emfacVersion}|${COMBINED_2015_2022_METHOD_VERSION}|Combined|Combined`;
}

/**
 * Set VMT data from Transportation page (called by Transportation page)
 * This includes user customizations and extrapolation
 */
export function setVMTDataFromTransportation(data: VMTDataPoint[], sourceLocation: string): void {
  const locationKey = normalizeVmtLocationKey(sourceLocation);
  setGlobalVMTData(data, locationKey);
  if (typeof window !== 'undefined') {
    window.vmtChartLocationKey = locationKey;
  }
  
  import('./policyBaselineCache').then(({ clearBaselineCache }) => {
    clearBaselineCache(sourceLocation);
  }).catch(() => {
    // Ignore errors if policyBaselineCache is not available (e.g., during initial load)
  });
}

/**
 * Compute VMT data using the EXACT same pipeline as VMTChart on the Transportation page.
 * Used when Policy/GHG page loads without prior visit to Transportation (no window.vmtChartData).
 * Combines: raw Replica for 2023/24/25, EMFAC backcasted for 2005-2014, getVMTDataWithScaling for 2015-2022.
 */
async function computeVMTIdenticalToTransportationPage(location: string, geofips: number): Promise<VMTDataPoint[]> {
  if (geofips === CALIFORNIA_STATE_GEOFIPS) {
    const inv = await fetchCaliforniaStateLdvVmtSeries(supabaseClient);
    if (inv.length > 0) {
      return inv.map(({ year, ldv_vmt }) => ({
        year,
        vmt: Math.round(ldv_vmt),
        source: 'ghg_inventory_precalculated',
        method_note:
          'California statewide ldv_vmt from ghg_inventory_precalculated (State row; same as Transportation page when inventory exists)',
      }));
    }
  }

  const emfac2005_2014Method: 'EMFAC2025' | 'Population' | 'Combined' = 'Combined';
  const backcastingMethod: 'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Combined' = 'Combined';
  const emfacVersion: '2021' | '2025' = '2025';
  const fleetVersion: '2021' | '2025' = '2025';

  const [rawVmtData, backcastedVmtData, emfacData] = await Promise.all([
    DataService.getVMTData(location, undefined, geofips),
    DataService.getVMTDataWithScaling(geofips, backcastingMethod, emfacVersion, fleetVersion),
    DataService.getEMFACBackcastingDataByGeofips(geofips, [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], emfac2005_2014Method).catch(() => [])
  ]);

  const rawPrivateAuto = rawVmtData.filter((d: any) => d.mode === 'PRIVATE_AUTO');
  const rawOnDemand = rawVmtData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');
  const backcastedPrivateAuto = backcastedVmtData.filter((d: any) => d.mode === 'PRIVATE_AUTO');
  const backcastedOnDemand = backcastedVmtData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');

  const vmt2015PrivateAuto = backcastedPrivateAuto.find((d: any) => d.year === 2015);
  const vmt2015OnDemand = backcastedOnDemand.find((d: any) => d.year === 2015);
  const vmt2015Total = (vmt2015PrivateAuto?.vmt || 0) + (vmt2015OnDemand?.vmt || 0);

  const emfacBackcastedData: any[] = [];
  const emfacYears = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014];

  if (vmt2015Total > 0 && emfacData.length > 0) {
    const sortedRatios = emfacData
      .filter((r: any) => emfacYears.includes(r.year))
      .sort((a: any, b: any) => b.year - a.year);

    const vmtByYear = new Map<number, number>();
    vmtByYear.set(2015, vmt2015Total);

    for (const emfacRecord of sortedRatios) {
      const nextYear = emfacRecord.year + 1;
      const nextYearVMT = vmtByYear.get(nextYear);
      if (nextYearVMT && emfacRecord.ratio) {
        const currentYearVMT = nextYearVMT * emfacRecord.ratio;
        vmtByYear.set(emfacRecord.year, currentYearVMT);
      }
    }

    const privateAutoRatio = vmt2015PrivateAuto ? vmt2015PrivateAuto.vmt / vmt2015Total : 0.8;
    const onDemandRatio = 1 - privateAutoRatio;

    for (const year of emfacYears) {
      const yearVMT = vmtByYear.get(year);
      if (yearVMT !== undefined && yearVMT > 0) {
        emfacBackcastedData.push({ year, mode: 'PRIVATE_AUTO', vmt: yearVMT * privateAutoRatio });
        emfacBackcastedData.push({ year, mode: 'ON_DEMAND_AUTO', vmt: yearVMT * onDemandRatio });
      }
    }
  }

  const emfacPrivateAuto = emfacBackcastedData.filter((d: any) => d.mode === 'PRIVATE_AUTO');
  const emfacOnDemand = emfacBackcastedData.filter((d: any) => d.mode === 'ON_DEMAND_AUTO');

  const allYears = Array.from(new Set([
    ...emfacPrivateAuto.map((d: any) => d.year),
    ...emfacOnDemand.map((d: any) => d.year),
    ...backcastedPrivateAuto.map((d: any) => d.year),
    ...backcastedOnDemand.map((d: any) => d.year),
    ...rawPrivateAuto.map((d: any) => d.year),
    ...rawOnDemand.map((d: any) => d.year)
  ])).sort((a, b) => a - b);

  const combinedData = allYears.map((year: number) => {
    if (year === 2023 || year === 2024 || year === 2025) {
      const pa = rawPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
      const od = rawOnDemand.find((d: any) => d.year === year)?.vmt || 0;
      return pa + od;
    }
    if (year >= 2005 && year <= 2014) {
      const pa = emfacPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
      const od = emfacOnDemand.find((d: any) => d.year === year)?.vmt || 0;
      return pa + od;
    }
    const pa = backcastedPrivateAuto.find((d: any) => d.year === year)?.vmt || 0;
    const od = backcastedOnDemand.find((d: any) => d.year === year)?.vmt || 0;
    return pa + od;
  });

  return allYears.map((year: number, index: number) => ({
    year,
    vmt: Math.round(combinedData[index]),
    source: 'transportation_pipeline',
    method_note: 'VMT computed using same pipeline as Transportation page VMT Chart'
  }));
}

/**
 * Get VMT data for GHG Inventory calculations
 * Returns data from Transportation page only - no fallback to Supabase
 * @param location - Location name
 * @param geofips - Optional geofips code
 * @param emfacVersion - Optional EMFAC version ('2021' or '2025') for backcasting data
 */
export async function getVMTDataForGHG(location: string, geofips?: number, emfacVersion: '2021' | '2025' = '2025'): Promise<VMTDataPoint[]> {
  const globalVMTData = getGlobalVMTData();
  const globalVMTDataLocationKey = getGlobalVMTDataLocationKey();

  // Transportation / Overview chart data — only reuse if it belongs to this location
  if (globalVMTData.length > 0 && vmtLocationKeysMatch(globalVMTDataLocationKey, location)) {
    return globalVMTData;
  }

  if (
    typeof window !== 'undefined' &&
    window.vmtChartData &&
    window.vmtChartData.length > 0 &&
    vmtLocationKeysMatch(window.vmtChartLocationKey ?? globalVMTDataLocationKey, location)
  ) {
    const legacyData = window.vmtChartData.map((d: any) => ({
      year: d.year,
      vmt: d.vmt || 0,
      source: 'transportation_page',
      method_note: 'Legacy window variable from Transportation page'
    }));
    return legacyData;
  }

  const key = vmtCacheKey(location, geofips, emfacVersion);
  const inFlightVMT = getInFlightVMT();
  if (inFlightVMT && inFlightVMT.key === key) {
    return inFlightVMT.promise;
  }

  // Fallback: Replicate the exact VMT calculation from the Transportation page (VMTChart).
  // Uses same data sources and combination logic: raw Replica for 2023/24/25, EMFAC backcasted for 2005-2014, getVMTDataWithScaling for 2015-2022.
  const promise = (async (): Promise<VMTDataPoint[]> => {
    try {
      if (!geofips) return [];
      const vmtData = await computeVMTIdenticalToTransportationPage(location, geofips);
      if (vmtData.length > 0) {
        const locationKey = normalizeVmtLocationKey(location);
        setGlobalVMTData(vmtData, locationKey);
        if (typeof window !== 'undefined') {
          window.vmtChartData = vmtData;
          window.vmtChartLocationKey = locationKey;
        }
      }
      return vmtData;
    } catch (error) {
      return [];
    }
  })().finally(() => {
    const current = getInFlightVMT();
    if (current?.key === key) setInFlightVMT(null);
  });

  setInFlightVMT({ key, promise });
  return promise;
}

/**
 * Get VMT data formatted for GHG calculations
 * Returns data in the format expected by GHG Inventory
 */
export async function getVMTDataForGHGCalculations(location: string): Promise<VMTDataResult> {
  const vmtData = await getVMTDataForGHG(location);
  
  if (vmtData.length === 0) {
    return {
      years: [],
      values: [],
      sources: [],
      providers: []
    };
  }

  // Sort by year and extract arrays
  const sortedData = vmtData.sort((a, b) => a.year - b.year);
  
  return {
    years: sortedData.map(d => d.year),
    values: sortedData.map(d => d.vmt || 0),
    sources: sortedData.map(d => d.source || 'unknown'),
    providers: sortedData.map(d => d.method_note || 'Unknown source')
  };
}

/**
 * Get current VMT data status
 */
export function getVMTDataStatus(): { hasData: boolean; recordCount: number; years: number[] } {
  const globalVMTData = getGlobalVMTData();
  return {
    hasData: globalVMTData.length > 0,
    recordCount: globalVMTData.length,
    years: globalVMTData.map(d => d.year).sort()
  };
}

/**
 * Check if VMT chart data is loaded for a place. When `forLocation` is set, requires a location match
 * so California does not treat Davis's cached series as ready.
 */
export function isVMTDataAvailable(forLocation?: string): boolean {
  const globalVMTData = getGlobalVMTData();
  const globalVMTDataLocationKey = getGlobalVMTDataLocationKey();
  const hasGlobal = globalVMTData.length > 0;
  const hasWindow =
    typeof window !== 'undefined' && window.vmtChartData && window.vmtChartData.length > 0;

  if (!forLocation) {
    return hasGlobal || !!hasWindow;
  }

  if (hasGlobal && vmtLocationKeysMatch(globalVMTDataLocationKey, forLocation)) {
    return true;
  }
  if (hasWindow && vmtLocationKeysMatch(window.vmtChartLocationKey ?? globalVMTDataLocationKey, forLocation)) {
    return true;
  }
  return false;
}